import {
  ref,
  onMounted,
  onUnmounted,
  nextTick,
} from 'vue';

export default function useSlideablePanel(panelRef) {
  const isDragging = ref(false);
  const panelHeight = ref(0);
  const VISIBLE_HEADER_HEIGHT = 80;

  let startY = 0;
  let startTransform = 0;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;

  const getCurrentTransform = () => {
    if (!panelRef.value) return 0;
    const { style } = panelRef.value;
    const { transform } = style;
    if (transform && transform.includes('translateY')) {
      const match = transform.match(/translateY\(([^)]+)px\)/);
      return match ? parseFloat(match[1]) : 0;
    }
    return 0;
  };

  const setTransform = (y) => {
    if (!panelRef.value) return;

    const minY = 0;
    const maxY = panelHeight.value - VISIBLE_HEADER_HEIGHT;
    const limitedY = Math.max(minY, Math.min(maxY, y));

    const panel = panelRef.value;
    panel.style.transform = `translateY(${limitedY}px)`;

    if (limitedY >= maxY - 10) {
      panel.classList.add('collapsed');
      panel.classList.remove('expanded');
    } else if (limitedY <= 10) {
      panel.classList.add('expanded');
      panel.classList.remove('collapsed');
    } else {
      panel.classList.remove('collapsed', 'expanded');
    }
  };

  const onTouchStart = (e) => {
    if (!panelRef.value) return;
    e.preventDefault();
    startY = e.touches[0].clientY;
    startTransform = getCurrentTransform();
    isDragging.value = true;
    panelRef.value.classList.add('dragging');

    lastY = startY;
    lastTime = Date.now();
    velocity = 0;
  };

  const onTouchMove = (e) => {
    if (!isDragging.value || !panelRef.value) return;
    e.preventDefault();

    const currentY = e.touches[0].clientY;
    const currentTime = Date.now();

    if (lastY !== 0 && lastTime !== 0) {
      const deltaY = currentY - lastY;
      const deltaTime = currentTime - lastTime;
      velocity = deltaY / deltaTime;
    }

    lastY = currentY;
    lastTime = currentTime;

    const deltaY = currentY - startY;
    const newTransform = startTransform + deltaY;

    setTransform(newTransform);
  };

  const onTouchEnd = (e) => {
    if (!isDragging.value || !panelRef.value) return;
    e.preventDefault();

    isDragging.value = false;
    panelRef.value.classList.remove('dragging');

    const currentTransform = getCurrentTransform();
    const maxY = panelHeight.value - VISIBLE_HEADER_HEIGHT;

    if (Math.abs(velocity) > 0.5) {
      if (velocity < 0) {
        setTransform(0);
      } else {
        setTransform(maxY);
      }
    } else if (currentTransform > maxY / 2) {
      setTransform(maxY);
    } else {
      setTransform(0);
    }

    startY = 0;
    lastY = 0;
    lastTime = 0;
    velocity = 0;
  };

  const updatePanelHeight = () => {
    if (panelRef.value) {
      panelHeight.value = panelRef.value.offsetHeight;
    }
  };

  const initDragHandle = () => {
    if (!panelRef.value) return;

    let dragHandle = panelRef.value.querySelector('.drag-handle');

    if (!dragHandle) {
      dragHandle = document.createElement('div');
      dragHandle.className = 'drag-handle';
      panelRef.value.insertBefore(dragHandle, panelRef.value.firstChild);
    }

    dragHandle.removeEventListener('touchstart', onTouchStart);
    dragHandle.removeEventListener('touchmove', onTouchMove);
    dragHandle.removeEventListener('touchend', onTouchEnd);
    dragHandle.removeEventListener('touchcancel', onTouchEnd);

    dragHandle.addEventListener('touchstart', onTouchStart, { passive: false });
    dragHandle.addEventListener('touchmove', onTouchMove, { passive: false });
    dragHandle.addEventListener('touchend', onTouchEnd);
    dragHandle.addEventListener('touchcancel', onTouchEnd);
  };

  const resetPanelStyles = () => {
    if (panelRef.value) {
      const panel = panelRef.value;
      panel.style.transform = '';
      panel.classList.remove('dragging', 'collapsed', 'expanded');
    }
  };

  const initMobileBehavior = () => {
    if (window.innerWidth <= 768) {
      nextTick(() => {
        updatePanelHeight();
        initDragHandle();

        if (panelRef.value && !panelRef.value.style.transform) {
          const maxY = panelHeight.value - VISIBLE_HEADER_HEIGHT;
          setTransform(maxY);
        }
      });
    } else {
      // En desktop, resetear estilos
      resetPanelStyles();
    }
  };

  const handleResize = () => {
    initMobileBehavior();
  };

  onMounted(() => {
    initMobileBehavior();
    window.addEventListener('resize', handleResize);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);

    if (panelRef.value) {
      const dragHandle = panelRef.value.querySelector('.drag-handle');
      if (dragHandle) {
        dragHandle.removeEventListener('touchstart', onTouchStart);
        dragHandle.removeEventListener('touchmove', onTouchMove);
        dragHandle.removeEventListener('touchend', onTouchEnd);
        dragHandle.removeEventListener('touchcancel', onTouchEnd);
      }
    }
  });

  return {
    isDragging,
    panelHeight,
    initMobileBehavior,
  };
}
